import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Gamepad2, Calculator, FlaskConical, BookOpen,
  Zap, Pizza, Hash, Shapes, Timer,
  Atom, Grid3X3, Heart, Cable, Table2,
  Shuffle, PenTool, ArrowRightLeft, Keyboard, BookText,
  Star, Trophy
} from 'lucide-react';

const GAMES = {
  math: [
    { id: 'math-blitz', name: 'Math Blitz', icon: <Zap className="w-8 h-8" />, description: 'Speed mental math! Answer before time runs out', difficulty: 'Easy-Hard', color: 'from-blue-400 to-blue-600' },
    { id: 'fraction-pizza', name: 'Fraction Pizza', icon: <Pizza className="w-8 h-8" />, description: 'Build fractions by slicing pizzas visually', difficulty: 'Easy', color: 'from-orange-400 to-red-500' },
    { id: 'number-ninja', name: 'Number Ninja', icon: <Hash className="w-8 h-8" />, description: 'Find the missing number in equations', difficulty: 'Medium', color: 'from-emerald-400 to-emerald-600' },
    { id: 'shape-builder', name: 'Shape Builder', icon: <Shapes className="w-8 h-8" />, description: 'Calculate area & perimeter of shapes', difficulty: 'Medium', color: 'from-violet-400 to-violet-600' },
    { id: 'times-table-race', name: 'Times Table Race', icon: <Timer className="w-8 h-8" />, description: 'Beat the clock on multiplication tables!', difficulty: 'Easy', color: 'from-cyan-400 to-cyan-600' },
  ],
  science: [
    { id: 'atom-builder', name: 'Atom Builder', icon: <Atom className="w-8 h-8" />, description: 'Build atoms with protons, neutrons & electrons', difficulty: 'Medium', color: 'from-pink-400 to-pink-600' },
    { id: 'element-match', name: 'Element Match', icon: <Grid3X3 className="w-8 h-8" />, description: 'Flip cards & match elements to symbols', difficulty: 'Easy', color: 'from-amber-400 to-amber-600' },
    { id: 'body-explorer', name: 'Body Explorer', icon: <Heart className="w-8 h-8" />, description: 'Place organs in the correct position', difficulty: 'Medium', color: 'from-rose-400 to-rose-600' },
    { id: 'circuit-connect', name: 'Circuit Connect', icon: <Cable className="w-8 h-8" />, description: 'Build working electrical circuits', difficulty: 'Hard', color: 'from-yellow-400 to-yellow-600' },
    { id: 'periodic-puzzle', name: 'Periodic Puzzle', icon: <Table2 className="w-8 h-8" />, description: 'Fill in the missing elements', difficulty: 'Hard', color: 'from-teal-400 to-teal-600' },
  ],
  english: [
    { id: 'word-scramble', name: 'Word Scramble', icon: <Shuffle className="w-8 h-8" />, description: 'Unscramble jumbled vocabulary words', difficulty: 'Easy', color: 'from-purple-400 to-purple-600' },
    { id: 'sentence-fixer', name: 'Sentence Fixer', icon: <PenTool className="w-8 h-8" />, description: 'Spot and fix grammar errors', difficulty: 'Medium', color: 'from-indigo-400 to-indigo-600' },
    { id: 'synonym-sprint', name: 'Synonym Sprint', icon: <ArrowRightLeft className="w-8 h-8" />, description: 'Match words to their synonyms fast!', difficulty: 'Easy', color: 'from-fuchsia-400 to-fuchsia-600' },
    { id: 'spell-bee', name: 'Spell Bee', icon: <Keyboard className="w-8 h-8" />, description: 'Type the correct spelling', difficulty: 'Medium', color: 'from-lime-500 to-green-600' },
    { id: 'story-builder', name: 'Story Builder', icon: <BookText className="w-8 h-8" />, description: 'Arrange sentences into a story', difficulty: 'Hard', color: 'from-sky-400 to-sky-600' },
  ]
};

const BrainArena = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="brain-arena-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#4D96FF] via-[#A493FF] to-[#FF6B6B] border-b-2 border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-white/80 hover:text-white font-semibold mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-xl rounded-3xl flex items-center justify-center border-2 border-white/30">
              <Gamepad2 className="w-12 h-12 text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-black text-white tracking-tight">Brain Arena</h1>
              <p className="text-xl text-white/90 font-medium mt-1">15 interactive games to make learning fun!</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Story Games Banner */}
        <div onClick={() => navigate('/games/story')} data-testid="story-games-banner"
          className="mb-10 bg-gradient-to-r from-[#1E293B] via-[#334155] to-[#1E293B] rounded-3xl p-8 cursor-pointer hover:scale-[1.02] transition-all shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-tr from-[#FF6B6B]/10 via-transparent to-[#4D96FF]/10"></div>
          <div className="relative flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-[#FFC03D] uppercase tracking-widest">NEW!</span>
              <h2 className="text-3xl font-black text-white mt-1">Story Quest Games</h2>
              <p className="text-white/70 mt-1">10 interactive story adventures! Be the hero, solve puzzles, save the day!</p>
            </div>
            <div className="text-6xl">🎬</div>
          </div>
        </div>

        {/* Math Games */}
        <SubjectSection
          title="Math Games"
          icon={<Calculator className="w-8 h-8 text-[#4D96FF]" />}
          bgColor="bg-[#D8E8FF]"
          games={GAMES.math}
          navigate={navigate}
        />

        {/* Science Games */}
        <SubjectSection
          title="Science Games"
          icon={<FlaskConical className="w-8 h-8 text-[#38E54D]" />}
          bgColor="bg-[#E8F5E9]"
          games={GAMES.science}
          navigate={navigate}
        />

        {/* English Games */}
        <SubjectSection
          title="English Games"
          icon={<BookOpen className="w-8 h-8 text-[#A493FF]" />}
          bgColor="bg-[#F3EBFF]"
          games={GAMES.english}
          navigate={navigate}
        />
      </div>
    </div>
  );
};

const SubjectSection = ({ title, icon, bgColor, games, navigate }) => (
  <div className="mb-10">
    <div className="flex items-center gap-3 mb-6">
      <div className={`w-14 h-14 ${bgColor} rounded-2xl flex items-center justify-center`}>
        {icon}
      </div>
      <h2 className="text-3xl font-black text-[#1E293B]">{title}</h2>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
      {games.map((game) => (
        <GameCard key={game.id} game={game} onClick={() => navigate(`/games/${game.id}`)} />
      ))}
    </div>
  </div>
);

const GameCard = ({ game, onClick }) => (
  <div
    data-testid={`game-card-${game.id}`}
    onClick={onClick}
    className="group cursor-pointer"
  >
    <div className={`bg-gradient-to-br ${game.color} rounded-3xl p-5 text-white shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 border-b-4 border-black/20 active:border-b-0 active:translate-y-1 h-full`}>
      <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
        {game.icon}
      </div>
      <h3 className="text-xl font-bold mb-1">{game.name}</h3>
      <p className="text-white/80 text-sm mb-3 leading-relaxed">{game.description}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold bg-white/20 px-3 py-1 rounded-xl">{game.difficulty}</span>
        <div className="flex items-center gap-1">
          <Star className="w-4 h-4" />
          <span className="text-xs font-bold">Play</span>
        </div>
      </div>
    </div>
  </div>
);

export default BrainArena;
